import { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';

export function useData(fetchFn, deps = []) {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchFn();
      setData(res.data?.data ?? res.data ?? []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, deps); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  return { data, loading, error, reload: load, setData };
}

/**
 * submitForm — prevents double submission.
 * Uses a module-level Set to track in-flight requests by key.
 * If the same key is already submitting, silently ignores the second click.
 */
const _inFlight = new Set();

export async function submitForm(apiFn, successMsg, reload, setOpen) {
  // Use function identity as key — same fn ref = same button
  const key = apiFn.toString().slice(0, 80);
  if (_inFlight.has(key)) {
    // Already submitting — ignore this click
    return false;
  }
  _inFlight.add(key);
  try {
    await apiFn();
    toast.success(successMsg);
    reload();
    setOpen && setOpen(false);
    return true;
  } catch (err) {
    toast.error(err.response?.data?.message || err.message || 'Error');
    return false;
  } finally {
    _inFlight.delete(key);
  }
}

/**
 * useSubmit — hook version for manual save handlers (GRN, Delivery, Invoice etc.)
 * Returns [submit, isSubmitting] where submit wraps any async fn.
 * The button should be disabled={isSubmitting} to prevent double-click.
 */
export function useSubmit() {
  const [submitting, setSubmitting] = useState(false);
  const lockRef = useRef(false); // extra guard for rapid clicks

  const submit = useCallback(async (apiFn, successMsg, reload, onClose) => {
    if (lockRef.current) return false; // already running
    lockRef.current = true;
    setSubmitting(true);
    try {
      await apiFn();
      toast.success(successMsg);
      reload && reload();
      onClose && onClose();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Error');
      return false;
    } finally {
      lockRef.current = false;
      setSubmitting(false);
    }
  }, []);

  return [submit, submitting];
}
